import { useEffect, useState } from "react";
import { FiCalendar, FiTrendingUp, FiZap, FiStar, FiAward, FiGift, FiFileText, FiPlusCircle, FiChevronDown } from "react-icons/fi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import PageHeader from "../../components/common/pageheader/PageHeader";
import Loader from "../../components/common/Loader/Loader";
import { getAddOns, getAllSubscriptions, getMySubscription, scheduleSubscriptionChange, setAutoRenew } from "../../services/subscriptionsApi";
import { initiatePayment } from "../../services/paymentsApi";
import ConfirmUpdateModal from "./components/ConfirmUpdateModal";
import ConfirmActionModal from "./components/ConfirmActionModal";
import PaymentModal from "../../components/payment/PaymentModal";
import styles from "./SubscriptionPage.module.css";
import { formatDate } from "../../utils/formatDateTime";
import { splitPlanLimits } from "../../utils/planLimits";
import { yearlyTermActive } from "../../utils/yearlyTerm";

const SubscriptionPage = () => {
  const { t } = useTranslation();
  const [mySubscription, setMySubscription] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [buyingAddOn, setBuyingAddOn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentContext, setPaymentContext] = useState(null);
  const [autoRenewSaving, setAutoRenewSaving] = useState(false);
  const [pendingAutoRenew, setPendingAutoRenew] = useState(null);
  // A lower monthly plan the user is about to schedule (warning modal open while non-null).
  const [downgradeTarget, setDowngradeTarget] = useState(null);
  const [schedulingChange, setSchedulingChange] = useState(false);
  // Indexes of usage rows whose used/total breakdown is expanded. Collapsed by default: the row
  // itself shows only what's LEFT, which stays a small number; used/total grow without bound.
  const [expandedUsage, setExpandedUsage] = useState(() => new Set());
  // Plan cards whose full limit table is expanded (by plan id). Cards lead with students + exams
  // only — the two numbers a director actually shops by.
  const [expandedPlanLimits, setExpandedPlanLimits] = useState(() => new Set());

  const togglePlanLimits = (planId) => {
    setExpandedPlanLimits((prev) => {
      const next = new Set(prev);
      if (next.has(planId)) next.delete(planId); else next.add(planId);
      return next;
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const [mySubRes, plansRes, addOnRes] = await Promise.allSettled([
      getMySubscription(),
      getAllSubscriptions(),
      getAddOns(),
    ]);

    // Add-ons are optional furniture. A tenant with no subscription row now falls back to the TRIAL
    // plan's catalogue rather than 404ing, but the call can still come back empty (a plan with no
    // packs seeded) — not worth a toast, just hide the section.
    setAddOns(
      addOnRes.status === "fulfilled" && Array.isArray(addOnRes.value) ? addOnRes.value : []
    );

    if (mySubRes.status === "fulfilled") {
      setMySubscription(mySubRes.value);
    } else {
      console.error("Error fetching subscription:", mySubRes.reason);
      setMySubscription(null);
    }

    if (plansRes.status === "fulfilled") {
      const plans = plansRes.value;
      setAllPlans(Array.isArray(plans) ? plans : (Array.isArray(plans?.data) ? plans.data : []));
    } else {
      console.error("Error fetching plans:", plansRes.reason);
      setAllPlans([]);
      toast.error(t("pages.subscription.loadPlansError"), { toastId: "plans-fetch-error" });
    }

    setLoading(false);
  };

  // While a yearly plan has prepaid months left, the backend rejects switching to ANY other
  // subscription plan — so those cards render disabled instead of erroring on click.
  const yearlyActive = yearlyTermActive(mySubscription);
  // "Başlanğıc" alone doesn't say WHAT was bought — the same tier name exists as a monthly and
  // a yearly product. Suffix the current-plan title with its billing period.
  const currentMonthlyPrice =
    mySubscription?.billingType === "MONTHLY" && mySubscription?.status === "ACTIVE"
      ? Number(allPlans.find((p) => p.code === mySubscription.code)?.price)
      : null;

  const handleScheduleChange = async (planCode) => {
    try {
      setSchedulingChange(true);
      await scheduleSubscriptionChange(planCode);
      toast.success(planCode
        ? t("pages.subscription.downgradeScheduledToast", { defaultValue: "Keçid planlaşdırıldı" })
        : t("pages.subscription.downgradeCancelledToast", { defaultValue: "Keçid ləğv edildi" }));
      setDowngradeTarget(null);
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error ||
        t("pages.subscription.downgradeScheduleError", { defaultValue: "Keçidi planlaşdırmaq mümkün olmadı" }));
    } finally {
      setSchedulingChange(false);
    }
  };

  const billingSuffix = mySubscription?.billingType === "YEARLY"
    ? t("pages.subscription.billingSuffixYearly", { defaultValue: "illik" })
    : mySubscription?.billingType === "MONTHLY"
      ? t("pages.subscription.billingSuffixMonthly", { defaultValue: "aylıq" })
      : null;
  const yearlyPlans = allPlans.filter((p) => p.billingType === "YEARLY");
  const monthlyPlans = allPlans.filter((p) => p.billingType === "MONTHLY");
  const oneTimePackages = allPlans.filter((p) => p.billingType === "ONE_TIME");
  const hasActivePlan = !!mySubscription?.code;
  // Backend now sends a list of { code, count, lastPurchasedAt } from the dedicated
  // tenant_one_time_purchases table (ACTIVE status). Used to (1) render chips with the
  // EXACT package names (and stack count when > 1) under the current subscription title
  // and (2) flip owned one-time cards to "Yenidən al" + a "Alınıb" badge.
  const purchasedPackagesRaw = mySubscription?.purchasedOneTimePackages || [];
  const purchasedOneTimeCodes = new Set(purchasedPackagesRaw.map((p) => p.code));
  const hasAnyOneTime = purchasedOneTimeCodes.size > 0;
  // Hydrate codes with the plan object (for name) and keep the count alongside.
  const ownedPackages = purchasedPackagesRaw
      .map((p) => ({
          plan: oneTimePackages.find((pl) => pl.code === p.code),
          count: p.count || 1,
      }))
      .filter((x) => x.plan);

  const handleBuyClick = (plan) => {
    setSelectedPlan(plan);
    setUpdateModalOpen(true);
  };

  const handleUpdateConfirm = async () => {
    if (!selectedPlan) return;
    const chosen = selectedPlan;
    try {
      setUpdating(true);
      await redirectToKapital({
        // Yearly plans ride the RENEWAL purpose too — the backend branches on the plan's
        // billing type (one prepaid 12-month term instead of a one-month charge).
        purpose: chosen.billingType === "ONE_TIME" ? "ONE_TIME_PACKAGE" : "RENEWAL",
        planCode: chosen.code,
        returnPath: "/settings/subscription",
      });
      // Browser navigates away.
    } catch (err) {
      console.error("Purchase initiate failed:", err);
      const errorMessage = err?.response?.data?.message || err?.response?.data?.error ||
        t("pages.subscription.paymentFailed");
      toast.error(errorMessage, { toastId: "payment-error" });
      setUpdating(false);
      setUpdateModalOpen(false);
    }
  };

  /**
   * Add-ons go straight to the hosted payment page — no confirm modal. Buying one changes nothing
   * about the subscription (it only tops a quota up), so there is no plan switch to warn about.
   * The pack resolves server-side against the tenant's current plan, so only the code is sent.
   */
  const handleBuyAddOn = async (addOn) => {
    if (buyingAddOn) return;
    setBuyingAddOn(addOn.code);
    try {
      await redirectToKapital({
        purpose: "ADDON_PURCHASE",
        addOnCode: addOn.code,
        returnPath: window.location.pathname + window.location.search,
      });
    } catch (err) {
      setBuyingAddOn(null);
      const errorMessage = err?.response?.data?.message || err?.response?.data?.error ||
        t("pages.subscription.paymentFailed");
      toast.error(errorMessage, { toastId: "addon-payment-error" });
    }
  };

  const redirectToKapital = async (initiationPayload) => {
    const res = await initiatePayment(initiationPayload);
    sessionStorage.setItem("gredus.kapitalPaymentTxId", String(res.paymentTransactionId));
    sessionStorage.setItem(
      "gredus.kapitalReturnPath",
      initiationPayload.returnPath || window.location.pathname + window.location.search
    );
    window.location.href = res.redirectUrl;
  };

  const handlePaymentConfirm = async () => {
    if (!paymentContext) return;
    try {
      setUpdating(true);
      if (paymentContext.kind === "RETRY_PAYMENT") {
        await redirectToKapital({ purpose: "RENEWAL", returnPath: "/settings/subscription" });
        return;
      }
    } catch (err) {
      console.error("Payment failed:", err);
      const errorMessage = err?.response?.data?.message || err?.response?.data?.error ||
        t("pages.subscription.paymentFailed");
      toast.error(errorMessage, { toastId: "payment-error" });
    } finally {
      setUpdating(false);
    }
  };

  const handleRetryPaymentClick = () => {
    if (!mySubscription) return;
    const plan = allPlans.find((p) => p.code === mySubscription.code);
    setPaymentContext({
      kind: "RETRY_PAYMENT",
      title: t("pages.subscription.retryPaymentTitle", { name: plan?.name || mySubscription.code }),
      amount: plan?.price,
    });
  };

  const handlePaymentCancel = () => {
    setPaymentContext(null);
    setUpdating(false);
  };

  const handleToggleAutoRenew = () => {
    if (!mySubscription || autoRenewSaving) return;
    setPendingAutoRenew(!mySubscription.autoRenew);
  };

  const handleAutoRenewCancel = () => {
    if (autoRenewSaving) return;
    setPendingAutoRenew(null);
  };

  const handleAutoRenewConfirm = async () => {
    if (pendingAutoRenew === null) return;
    const next = pendingAutoRenew;
    try {
      setAutoRenewSaving(true);
      await setAutoRenew(next);
      setMySubscription({ ...mySubscription, autoRenew: next });
      toast.success(next ? t("pages.subscription.autoRenewActiveToast") : t("pages.subscription.autoRenewOffToast"), {
        toastId: "auto-renew-toggle",
      });
      setPendingAutoRenew(null);
    } catch (err) {
      const msg = err?.response?.data?.message || t("pages.subscription.autoRenewError");
      toast.error(msg);
    } finally {
      setAutoRenewSaving(false);
    }
  };

  const handleUpdateCancel = () => {
    setUpdateModalOpen(false);
    setSelectedPlan(null);
  };

  const getStatusText = (status) => ({
    TRIAL: t("pages.subscription.statusTrial"),
    ACTIVE: t("pages.subscription.statusActive"),
    INACTIVE: t("pages.subscription.statusInactive"),
    EXPIRED: t("pages.subscription.statusExpired"),
    PENDING: t("pages.subscription.statusPending"),
    PAYMENT_FAILED: t("pages.subscription.statusPaymentFailed"),
  }[status] || status);

  const getStatusClass = (status) => ({
    TRIAL: styles.statusPending,
    ACTIVE: styles.statusActive,
    INACTIVE: styles.statusInactive,
    EXPIRED: styles.statusExpired,
    PENDING: styles.statusPending,
    PAYMENT_FAILED: styles.statusExpired,
  }[status] || "");

  const getPlanIcon = (code) => {
    if (!code) return <FiStar />;
    if (code.includes("STARTER")) return <FiStar />;
    if (code.includes("STANDARD")) return <FiAward />;
    if (code.includes("ENTERPRISE")) return <FiZap />;
    return <FiStar />;
  };

  const getPlanIconGradient = (code) => {
    if (code && code.includes("STARTER")) return "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)";
    if (code && code.includes("STANDARD")) return "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)";
    if (code && code.includes("ENTERPRISE")) return "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)";
    return "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)";
  };

  const getPlanGradient = (code) => {
    if (code && code.includes("STARTER")) return "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)";
    if (code && code.includes("STANDARD")) return "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)";
    if (code && code.includes("ENTERPRISE")) return "linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)";
    return "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)";
  };

  const toggleUsageDetails = (idx) => {
    setExpandedUsage((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  /**
   * Each row leads with what's LEFT (a small, bounded number) instead of "used / total", whose
   * halves both grow over time and dominate the card. The used/total breakdown is one tap away —
   * the whole row toggles it — and also sits in a hover tooltip for desktop.
   */
  const renderUsage = () => {
    if (!mySubscription?.usage?.length) return null;
    return (
      <div className={styles.usageSection}>
        <div className={styles.usageHeader}>
          <FiTrendingUp className={styles.usageIcon} />
          <h5 className={styles.usageTitle}>{t("pages.subscription.usage")}</h5>
        </div>
        {mySubscription.usage.map((u, idx) => {
          const unlimited = u.limit === -1 || u.limit == null;
          const pct = unlimited || !u.limit ? 0 : Math.min(100, Math.round((u.used / u.limit) * 100));
          // Video quota is tracked in seconds on the backend; show it in minutes (1 decimal).
          const isVideo = u.limitCode === "VIDEO_MINUTES_LIMIT";
          const fmt = (v) => (isVideo ? +(v / 60).toFixed(1) : v);
          const remaining = unlimited
            ? null
            : Math.max(0, typeof u.remaining === "number" ? u.remaining : u.limit - u.used);
          const depleted = !unlimited && remaining === 0;
          const isOpen = expandedUsage.has(idx);
          const usedTotalText = `${t("pages.subscription.usedLabel", { defaultValue: "İstifadə olunub" })}: ${fmt(u.used)} / ${unlimited ? "∞" : fmt(u.limit)}`;
          return (
            <div key={idx} className={styles.usageItem}>
              <button
                type="button"
                className={styles.usageRowBtn}
                onClick={() => toggleUsageDetails(idx)}
                aria-expanded={isOpen}
                title={usedTotalText}
              >
                <span className={styles.usageItemLabel}>{u.limitName}</span>
                <span className={styles.usageRowRight}>
                  <span className={`${styles.usageRemainBadge} ${depleted ? styles.usageRemainBadgeEmpty : ""}`}>
                    {unlimited
                      ? t("pages.subscription.unlimited", { defaultValue: "Limitsiz" })
                      : `${fmt(remaining)} ${t("pages.subscription.remainingShort", { defaultValue: "qalıb" })}`}
                  </span>
                  <FiChevronDown className={`${styles.usageChevron} ${isOpen ? styles.usageChevronOpen : ""}`} />
                </span>
              </button>
              {!unlimited && (
                <div style={{ height: 8, borderRadius: 2, background: "#e2e8f0", overflow: "hidden", marginTop: 6 }}>
                  <div style={{
                    height: "100%",
                    width: `${pct}%`,
                    background: pct >= 100 ? "#dc2626" : "var(--gredus-primary, #3B82F6)",
                    borderRadius: 2,
                    transition: "width 0.2s ease",
                  }} />
                </div>
              )}
              {isOpen && (
                <div className={styles.usageDetails}>
                  <span>
                    {t("pages.subscription.usedLabel", { defaultValue: "İstifadə olunub" })}:{" "}
                    <strong>{fmt(u.used)}</strong>
                  </span>
                  <span>
                    {t("pages.subscription.totalLabel", { defaultValue: "Ümumi" })}:{" "}
                    <strong>{unlimited ? "∞" : fmt(u.limit)}</strong>
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderPlanCard = (plan, oneTime) => {
    const isYearlyPlan = plan.billingType === "YEARLY";
    // 790 ₼ for 12 months vs 12 paid months of the same tier — the strikethrough makes
    // "two months free" visible without a word of copy.
    const monthlyTwin = isYearlyPlan
      ? allPlans.find((p) => p.code === String(plan.code).replace("YEARLY_", "MONTHLY_"))
      : null;
    const fullYearPrice = monthlyTwin ? Math.round(Number(monthlyTwin.price) * 12) : null;
    const isCurrent = !oneTime && hasActivePlan && mySubscription.code === plan.code;
    const blockedByYearly = !oneTime && yearlyActive && mySubscription.code !== plan.code;
    // A cheaper monthly plan while an ACTIVE monthly runs is a SCHEDULED downgrade, not a
    // purchase: the paid month keeps its tier until it ends, so there is nothing to charge now.
    const isMonthlyDowngrade = !oneTime
      && plan.billingType === "MONTHLY"
      && currentMonthlyPrice != null
      && !isCurrent
      && Number(plan.price) < currentMonthlyPrice;
    const isScheduledTarget = mySubscription?.pendingPlanCode === plan.code;
    const { primary, rest, collapsible } = splitPlanLimits(plan.limits);
    const limitsOpen = expandedPlanLimits.has(plan.id);
    const visibleLimits = collapsible ? (limitsOpen ? [...primary, ...rest] : primary) : (plan.limits || []);
    const isExpired = mySubscription?.status === "EXPIRED" || mySubscription?.status === "PAYMENT_FAILED";
    const showRenewOnCurrent = isCurrent && isExpired;
    const isOwnedOneTime = oneTime && purchasedOneTimeCodes.has(plan.code);
    let buttonLabel;
    if (oneTime) {
      buttonLabel = isOwnedOneTime
        ? t("pages.subscription.buyAgain", { defaultValue: "Yenidən al" })
        : t("pages.subscription.buy", { defaultValue: "Al" });
    } else if (showRenewOnCurrent) {
      buttonLabel = t("pages.subscription.renew");
    } else if (!isCurrent) {
      buttonLabel = t("pages.subscription.subscribe", { defaultValue: "Abunə ol" });
    }
    return (
      <div key={plan.id} style={{ position: "relative" }} className={`${styles.planCard} ${isCurrent && !isExpired ? styles.selectedPlan : ""}`}>
        {isOwnedOneTime && (
          <span
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              padding: "4px 10px",
              background: "#10b981",
              color: "#ffffff",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.02em",
              borderRadius: 6,
              boxShadow: "0 4px 12px -4px rgba(16,185,129,0.45)",
              zIndex: 1,
            }}
          >
            {t("pages.subscription.purchasedBadge", { defaultValue: "Alınıb" })}
          </span>
        )}
        <div className={styles.planHeader} style={{ background: getPlanGradient(plan.code) }}>
          <div className={styles.planHeaderTop}>
            <div className={styles.planIcon} style={{ background: getPlanIconGradient(plan.code) }}>
              {getPlanIcon(plan.code)}
            </div>
            <h3 className={styles.planName}>{plan.name}</h3>
          </div>
          <div className={styles.planPriceWrapper}>
            {fullYearPrice != null && fullYearPrice > Number(plan.price) && (
              <span style={{ textDecoration: "line-through", color: "#94a3b8", fontSize: 15, fontWeight: 600, marginRight: 6 }}>
                {fullYearPrice}
              </span>
            )}
            <span className={styles.planPrice}>{plan.price}</span>
            <span className={styles.planCurrency}>{t("pages.subscription.currency")}</span>
            <span className={styles.planPeriod}>
              {isYearlyPlan
                ? t("pages.subscription.perYear", { defaultValue: "/il" })
                : oneTime
                  ? t("pages.subscription.oneTimeLabel", { defaultValue: "birdəfəlik" })
                  : t("pages.subscription.perMonth")}
            </span>
          </div>
        </div>

        <div className={styles.planFooter}>
          {isCurrent && !showRenewOnCurrent ? (
            <button className={styles.purchaseBtn} disabled>
              {t("pages.subscription.currentPlanBtn")}
            </button>
          ) : isScheduledTarget ? (
            <>
              <button className={styles.purchaseBtn} disabled>
                {t("pages.subscription.downgradeScheduledBadge", { defaultValue: "Planlaşdırılıb" })}
              </button>
              <div style={{ marginTop: 8, fontSize: 12.5, color: "#64748b", textAlign: "center" }}>
                {t("pages.subscription.pendingChangeShort", {
                  defaultValue: "{{date}} tarixindən qüvvədə olacaq",
                  date: formatDate(mySubscription.endDate),
                })}
              </div>
            </>
          ) : isMonthlyDowngrade ? (
            <button
              className={styles.purchaseBtn}
              onClick={() => setDowngradeTarget(plan)}
              disabled={updating || schedulingChange}
            >
              {t("pages.subscription.scheduleDowngradeBtn", { defaultValue: "Aşağı plana keç" })}
            </button>
          ) : blockedByYearly ? (
            <>
              <button className={styles.purchaseBtn} disabled>
                {buttonLabel}
              </button>
              <div style={{ marginTop: 8, fontSize: 12.5, color: "#64748b", textAlign: "center" }}>
                {t("pages.subscription.licenseActiveUntil", {
                  defaultValue: "İllik abunəliyiniz {{date}}-dək aktivdir",
                  date: formatDate(mySubscription.paidThrough),
                })}
              </div>
            </>
          ) : (
            <button className={styles.purchaseBtn} onClick={() => handleBuyClick(plan)} disabled={updating}>
              {updating ? t("pages.subscription.waitingPlease") : buttonLabel}
            </button>
          )}
        </div>

        <div className={styles.planBody}>
          <ul className={styles.planLimits}>
            {visibleLimits.map((limit, idx) => (
              <li key={idx} className={styles.limitItem}>
                <span className={styles.limitCheck}></span>
                <span className={styles.limitText}>
                  {/* Video quota is stored in SECONDS (V23); every other limit is a plain count. */}
                  {limit.limitName}: <strong>{limit.limitValue === -1 ? "∞"
                    : limit.limitCode === "VIDEO_MINUTES_LIMIT" ? Math.round(limit.limitValue / 60)
                    : limit.limitValue}</strong>
                </span>
              </li>
            ))}
          </ul>
          {collapsible && (
            <button
              type="button"
              aria-expanded={limitsOpen}
              onClick={() => togglePlanLimits(plan.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "none",
                border: "none",
                padding: 0,
                marginTop: 10,
                color: "var(--gredus-primary, #2563eb)",
                fontWeight: 600,
                fontSize: 13.5,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {t("pages.subscription.allLimits", { defaultValue: "Bütün limitlər" })}
              <FiChevronDown style={{ transition: "transform 0.18s ease", transform: limitsOpen ? "rotate(180deg)" : "none" }} />
            </button>
          )}
        </div>
      </div>
    );
  };

  /**
   * An add-on pack. Unlike a plan it carries no `id` and no `limits` — the server returns
   * {code, name, description, price, items:[{limitCode, name, value}]} — and it's keyed by code,
   * which is unique within the tenant's own plan (packs are stored one row per plan).
   */
  const renderAddOnCard = (addOn) => (
    <div key={addOn.code} className={styles.planCard}>
      <div className={styles.planHeader} style={{ background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)" }}>
        <div className={styles.planHeaderTop}>
          <div className={styles.planIcon} style={{ background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)" }}>
            <FiPlusCircle />
          </div>
          <h3 className={styles.planName}>{addOn.name}</h3>
        </div>
        <div className={styles.planPriceWrapper}>
          <span className={styles.planPrice}>{addOn.price}</span>
          <span className={styles.planCurrency}>{t("pages.subscription.currency")}</span>
          <span className={styles.planPeriod}>
            {t("pages.subscription.oneTimeLabel", { defaultValue: "birdəfəlik" })}
          </span>
        </div>
      </div>

      <div className={styles.planFooter}>
        <button
          className={styles.purchaseBtn}
          onClick={() => handleBuyAddOn(addOn)}
          disabled={!!buyingAddOn || updating}
        >
          {buyingAddOn === addOn.code
            ? t("pages.subscription.waitingPlease")
            : t("pages.subscription.buy", { defaultValue: "Al" })}
        </button>
      </div>

      <div className={styles.planBody}>
        <ul className={styles.planLimits}>
          {(addOn.items || []).map((item, idx) => (
            <li key={idx} className={styles.limitItem}>
              <span className={styles.limitCheck}></span>
              <span className={styles.limitText}>
                {item.name}: <strong>+{item.value}</strong>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader title={t("pages.subscription.title")} />

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <Link
          to="/settings/invoices"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            color: "var(--gredus-primary, #2563eb)",
            fontWeight: 600,
            fontSize: 14,
            textDecoration: "none",
          }}
        >
          <FiFileText /> {t("pages.subscription.viewInvoices")}
        </Link>
      </div>

      {loading && <Loader fullscreen />}

      {/* Current entitlement (monthly plan if any) + usage meters */}
      {mySubscription && (
        <div className={styles.currentSubscription}>
          <div className={styles.currentCard}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>{t("pages.subscription.currentSubscription")}</h3>
              <div className={styles.cardSubtitle}>{t("pages.subscription.currentSubscriptionDescription")}</div>
            </div>
            <div className={styles.currentCardHeader}>
              <div className={styles.currentCardHeaderLeft}>
                <div className={styles.currentCardIcon} style={{ background: hasActivePlan ? getPlanIconGradient(mySubscription.code) : "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}>
                  {hasActivePlan ? getPlanIcon(mySubscription.code) : <FiGift />}
                </div>
                <div>
                  {/* The title only ever names the monthly subscription (or its absence) — owned
                      one-time packages are supplementary and live in the chips row below. Joining
                      their names into the title produced headings like "Standart × 3". */}
                  <h4 className={styles.currentPlanName}>
                    {hasActivePlan
                      ? `${allPlans.find((p) => p.code === mySubscription.code)?.name || mySubscription.code}${billingSuffix ? ` (${billingSuffix})` : ""}`
                      : ownedPackages.length > 0
                        ? t("pages.subscription.noActivePlan", { defaultValue: "Aktiv abunəlik yoxdur" })
                        : t("pages.subscription.freeTrialPlan", { defaultValue: "Pulsuz sınaq paketi" })}
                  </h4>
                  {hasActivePlan && (
                    <span className={`${styles.statusBadge} ${getStatusClass(mySubscription.status)}`}>
                      {getStatusText(mySubscription.status)}
                    </span>
                  )}
                  {ownedPackages.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>
                        {t("pages.subscription.ownedPackagesLabel", { defaultValue: "Birdəfəlik paketləriniz" })}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {ownedPackages.map(({ plan, count }) => (
                          <span
                            key={plan.id}
                            title={plan.name}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "5px 12px",
                              background: "#ecfdf5",
                              color: "#047857",
                              borderRadius: 6,
                              fontSize: 12.5,
                              fontWeight: 600,
                              border: "1px solid #d1fae5",
                              lineHeight: 1.3,
                            }}
                          >
                            <FiGift size={12} />
                            {plan.name}
                            {count > 1 && (
                              <span style={{
                                marginLeft: 2,
                                padding: "1px 7px",
                                background: "#047857",
                                color: "#ffffff",
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 700,
                              }}>
                                ×{count}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.currentCardBody}>
              {hasActivePlan && (
                <>
                  {mySubscription.billingType === "YEARLY" ? (
                    <>
                      {/* startDate/endDate are the CURRENT MONTHLY CYCLE of the yearly plan
                          (limits refill on it); the paid term runs until paidThrough. */}
                      <div className={styles.infoRow}>
                        <div className={styles.infoIcon}><FiCalendar /></div>
                        <span className={styles.infoLabel}>{t("pages.subscription.currentPeriod", { defaultValue: "Cari dövr" })}</span>
                        <span className={styles.infoValue}>
                          {formatDate(mySubscription.startDate)} – {formatDate(mySubscription.endDate)}
                        </span>
                      </div>
                      {mySubscription.paidThrough && (
                        <div className={styles.infoRow}>
                          <div className={styles.infoIcon}><FiCalendar /></div>
                          <span className={styles.infoLabel}>{t("pages.subscription.licenseValidUntil", { defaultValue: "İllik abunəliyin bitmə tarixi" })}</span>
                          <span className={styles.infoValue}>{formatDate(mySubscription.paidThrough)}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className={styles.infoRow}>
                        <div className={styles.infoIcon}><FiCalendar /></div>
                        <span className={styles.infoLabel}>{t("pages.subscription.startDate")}</span>
                        <span className={styles.infoValue}>{formatDate(mySubscription.startDate)}</span>
                      </div>
                      <div className={styles.infoRow}>
                        <div className={styles.infoIcon}><FiCalendar /></div>
                        <span className={styles.infoLabel}>{t("pages.subscription.endDate")}</span>
                        <span className={styles.infoValue}>{formatDate(mySubscription.endDate)}</span>
                      </div>
                    </>
                  )}
                  {/* Only on an ACTIVE monthly subscription — the one state where the flag steers
                      the next auto-charge. TRIAL rows are never auto-charged (the flag is inert),
                      EXPIRED rows are never revisited by the sweep, and PAYMENT_FAILED retries are
                      bounded (max 3) and resolved through the retry button instead. */}
                  {mySubscription.status === "ACTIVE" && mySubscription.billingType !== "YEARLY" && (
                    <div className={styles.infoRow} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <span className={styles.infoLabel}>{t("pages.subscription.autoRenew")}</span>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                        <input type="checkbox" checked={!!mySubscription.autoRenew} onChange={handleToggleAutoRenew} disabled={autoRenewSaving} />
                        <span>{mySubscription.autoRenew ? t("pages.subscription.autoRenewActive") : t("pages.subscription.autoRenewOff")}</span>
                      </label>
                    </div>
                  )}
                  {mySubscription.pendingPlanCode && (
                    <div className={styles.infoRow} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13.5, color: "#9a5b00", fontWeight: 600 }}>
                        {t("pages.subscription.pendingChangeLine", {
                          defaultValue: "{{current}} planınız {{date}} tarixində {{target}} olaraq dəyişəcək.",
                          current: allPlans.find((p) => p.code === mySubscription.code)?.name || mySubscription.code,
                          target: allPlans.find((p) => p.code === mySubscription.pendingPlanCode)?.name || mySubscription.pendingPlanCode,
                          date: formatDate(mySubscription.endDate),
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleScheduleChange(null)}
                        disabled={schedulingChange}
                        style={{ background: "none", border: "none", padding: 0, color: "var(--gredus-primary, #2563eb)", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}
                      >
                        {t("pages.subscription.cancelPendingChange", { defaultValue: "Keçidi ləğv et" })}
                      </button>
                    </div>
                  )}
                  {mySubscription.status === "PAYMENT_FAILED" && (
                    <div className={styles.infoRow} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <button className={styles.purchaseBtn} onClick={handleRetryPaymentClick} disabled={updating}>
                        {updating ? t("pages.subscription.waitingPlease") : t("pages.subscription.retryPayment")}
                      </button>
                    </div>
                  )}
                </>
              )}
              {renderUsage()}
            </div>
          </div>
        </div>
      )}

      {/* Yearly plans — the default offer, shown first and marked recommended. */}
      {yearlyPlans.length > 0 && (
        <div className={styles.allPlans}>
          <div className={styles.plansHeaderCard}>
            <div className={styles.plansHeaderContent}>
              <h3 className={styles.plansHeaderTitle} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {t("pages.subscription.yearlyPlans", { defaultValue: "İllik abunəlik" })}
                <span style={{
                  padding: "3px 10px", background: "#2563eb", color: "#fff",
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.03em", borderRadius: 5,
                }}>
                  {t("pages.subscription.recommendedBadge", { defaultValue: "Tövsiyə olunur" })}
                </span>
                <span style={{
                  padding: "3px 10px", background: "#ecfdf5", color: "#047857",
                  border: "1px solid #d1fae5", fontSize: 11, fontWeight: 700, borderRadius: 5,
                }}>
                  {t("pages.subscription.twoMonthsFree", { defaultValue: "2 ay pulsuz" })}
                </span>
              </h3>
              <div className={styles.plansHeaderSubtitle}>{t("pages.subscription.yearlyPlansSubtitle", { defaultValue: "12 ay — 10 ayın qiymətinə. 2 ay pulsuz, limitlər hər ay yenilənir" })}</div>
            </div>
            <div className={styles.plansGrid}>
              {yearlyPlans.map((plan) => renderPlanCard(plan, false))}
            </div>
          </div>
        </div>
      )}

      {/* Monthly plans */}
      <div className={styles.allPlans}>
        <div className={styles.plansHeaderCard}>
          <div className={styles.plansHeaderContent}>
            <h3 className={styles.plansHeaderTitle}>{t("pages.subscription.monthlyPlans", { defaultValue: "Aylıq abunəliklər" })}</h3>
            <div className={styles.plansHeaderSubtitle}>{t("pages.subscription.monthlyPlansSubtitle", { defaultValue: "Hər ay yenilənən limitlər" })}</div>
          </div>
          <div className={styles.plansGrid}>
            {monthlyPlans.map((plan) => renderPlanCard(plan, false))}
          </div>
        </div>
      </div>

      {/* One-time packages — campaign credits, not a cheaper way to run a center: the exams never
          expire and cost deliberately more per exam than the subscriptions. */}
      <div className={styles.allPlans}>
        <div className={styles.plansHeaderCard}>
          <div className={styles.plansHeaderContent}>
            <h3 className={styles.plansHeaderTitle}>{t("pages.subscription.oneTimePackages", { defaultValue: "Birdəfəlik paketlər" })}</h3>
            <div className={styles.plansHeaderSubtitle}>{t("pages.subscription.oneTimePackagesSubtitle", { defaultValue: "Bir imtahan kampaniyası üçün kreditlər — vaxtı bitmir, istifadə olunana qədər qalır" })}</div>
          </div>
          <div className={styles.plansGrid}>
            {oneTimePackages.map((plan) => renderPlanCard(plan, true))}
          </div>
          <div style={{ marginTop: 10, fontSize: 12.5, color: "#64748b" }}>
            {t("pages.subscription.oneTimePackagesNote", { defaultValue: "İmtahanların istifadə müddəti yoxdur. Daimi istifadə üçün illik abunəlik daha sərfəlidir." })}
          </div>
        </div>
      </div>

      {/* Xüsusi — the tier without numbers: schools and multi-hundred-student centers talk to
          us instead of picking a card. One full-width band below every plan group. */}
      <div className={styles.allPlans}>
        <div
          className={styles.plansHeaderCard}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}
        >
          <div>
            <h3 className={styles.plansHeaderTitle}>{t("pages.subscription.customPlanTitle", { defaultValue: "Xüsusi" })}</h3>
            <div className={styles.plansHeaderSubtitle}>
              {t("pages.subscription.customPlanText", { defaultValue: "Məktəb və ya böyük tədris mərkəzisiniz? Sizin üçün xüsusi paket hazırlayaq." })}
            </div>
          </div>
          <a
            href={`mailto:info@gredus.com?subject=${encodeURIComponent(t("pages.subscription.customPlanMailSubject", { defaultValue: "Xüsusi paket sorğusu" }))}`}
            className={styles.purchaseBtn}
            style={{ width: "auto", display: "inline-flex", alignItems: "center", justifyContent: "center", textDecoration: "none", whiteSpace: "nowrap", flexShrink: 0 }}
          >
            {t("pages.subscription.contactUs", { defaultValue: "Əlaqə saxlayın" })}
          </a>
        </div>
      </div>

      {/* Add-on packs — top up a single limit without changing plan. Hidden when the tenant's plan
          has none (TRIAL), so the section never renders as an empty shell. */}
      {addOns.length > 0 && (
        <div className={styles.allPlans}>
          <div className={styles.plansHeaderCard}>
            <div className={styles.plansHeaderContent}>
              <h3 className={styles.plansHeaderTitle}>{t("pages.subscription.addOnPacks", { defaultValue: "Əlavə paketlər" })}</h3>
              <div className={styles.plansHeaderSubtitle}>{t("pages.subscription.addOnPacksSubtitle", { defaultValue: "Planı dəyişmədən ayrıca limitə əlavə edin" })}</div>
            </div>
            <div className={styles.plansGrid}>
              {addOns.map(renderAddOnCard)}
            </div>
          </div>
        </div>
      )}

      <ConfirmUpdateModal
        isOpen={updateModalOpen}
        plan={selectedPlan}
        subscription={mySubscription}
        currentPlanPrice={allPlans.find((p) => p.code === mySubscription?.code)?.price}
        onConfirm={handleUpdateConfirm}
        onCancel={handleUpdateCancel}
        loading={updating}
      />

      <PaymentModal
        isOpen={!!paymentContext}
        title={paymentContext?.title}
        amount={paymentContext?.amount}
        onConfirm={handlePaymentConfirm}
        onCancel={handlePaymentCancel}
        loading={updating}
      />

      <ConfirmActionModal
        isOpen={downgradeTarget !== null}
        title={t("pages.subscription.downgradeScheduleTitle", { defaultValue: "Keçidi planlaşdır" })}
        message={(() => {
          if (!downgradeTarget) return "";
          const currentName = allPlans.find((p) => p.code === mySubscription?.code)?.name || mySubscription?.code;
          const date = formatDate(mySubscription?.endDate);
          let msg = t("pages.subscription.downgradeScheduleMessage", {
            defaultValue: "{{current}} planınız {{date}} tarixində {{target}} olaraq dəyişəcək. Cari dövrün sonuna qədər limitləriniz dəyişməz; geri qaytarma tələb olunmur — bu ayı artıq almısınız.",
            current: currentName, target: downgradeTarget.name, date,
          });
          // The warning does most of the work: if their live student count exceeds the target
          // cap, say exactly what will and won't keep working — before they commit.
          const liveStudents = (mySubscription?.usage || []).find((u) => u.limitCode === "STUDENT_COUNT_LIMIT")?.used;
          const targetCap = (downgradeTarget.limits || []).find((l) => l.limitCode === "STUDENT_COUNT_LIMIT")?.limitValue;
          if (liveStudents != null && targetCap != null && targetCap !== -1 && liveStudents > targetCap) {
            msg += "\n\n" + t("pages.subscription.downgradeStudentsWarning", {
              defaultValue: "Hazırda {{count}} tələbəniz var. {{target}} planında limit {{cap}}-dir — mövcud tələbələr qalacaq, lakin {{date}} tarixindən yeni tələbə əlavə edə bilməyəcəksiniz.",
              count: liveStudents, cap: targetCap, target: downgradeTarget.name, date,
            });
          }
          return msg;
        })()}
        confirmLabel={t("common.confirm", { defaultValue: "Təsdiq et" })}
        cancelLabel={t("common.cancel", { defaultValue: "Ləğv et" })}
        loadingLabel={t("pages.subscription.autoRenewSaving", { defaultValue: "Yadda saxlanılır..." })}
        loading={schedulingChange}
        tone="danger"
        onConfirm={() => handleScheduleChange(downgradeTarget.code)}
        onCancel={() => setDowngradeTarget(null)}
      />

      <ConfirmActionModal
        isOpen={pendingAutoRenew !== null}
        title={
          pendingAutoRenew
            ? t("pages.subscription.autoRenewEnableTitle", { defaultValue: "Avtomatik yenilənməni aktiv et" })
            : t("pages.subscription.autoRenewDisableTitle", { defaultValue: "Avtomatik yenilənməni söndür" })
        }
        message={
          pendingAutoRenew
            ? t("pages.subscription.autoRenewEnableMessage", {
                defaultValue: "Abunəliyiniz hər ay yadda saxlanılmış kartdan avtomatik olaraq yenilənəcək. Davam etmək istəyirsiniz?",
              })
            : t("pages.subscription.autoRenewDisableMessage", {
                defaultValue: "Avtomatik yenilənmə söndürülərsə, müddət bitdikdə abunəliyiniz dayandırılacaq. Davam etmək istəyirsiniz?",
              })
        }
        confirmLabel={t("common.confirm", { defaultValue: "Təsdiq et" })}
        cancelLabel={t("common.cancel", { defaultValue: "Ləğv et" })}
        loadingLabel={t("pages.subscription.autoRenewSaving", { defaultValue: "Yadda saxlanılır..." })}
        loading={autoRenewSaving}
        tone={pendingAutoRenew ? "default" : "danger"}
        onConfirm={handleAutoRenewConfirm}
        onCancel={handleAutoRenewCancel}
      />
    </div>
  );
};

export default SubscriptionPage;
